import { type Dispatch } from 'redux';
import { readInlineData, graphql } from 'react-relay';
import { updateUriRef } from '../../actions/filterActions.ts';
import { QP_PAGE } from '../../constants/queryParamConstants.ts';
import {
    isDeliveredByFilter,
    SHIPPING_TO_FILTER,
} from '../SbSharedRefineMenu/sbSharedRefineMenuHelpers.ts';

import { type sbSharedAppliedFiltersHelpers_itemSearch$key } from './__generated__/sbSharedAppliedFiltersHelpers_itemSearch.graphql.ts';

export function getAppliedFilterValues<
    T extends { values: ReadonlyArray<Record<string, string | null> | null> | null },
>({
    appliedFilter,
    isBatchApplied,
}: {
    appliedFilter: T;
    isBatchApplied: boolean;
}): T['values'] | null | undefined {
    return isBatchApplied ? appliedFilter?.values?.slice(0, 1) : appliedFilter.values;
}

export function closeAppliedFilterHandler(dispatch: Dispatch) {
    return function ({
        filterName,
        filterValue,
        generatedUriRef,
        isBatchApplied,
        event,
    }: {
        filterName: string;
        filterValue?: Record<string, unknown> | null;
        generatedUriRef?: string | null;
        isBatchApplied: boolean;
        event: React.MouseEvent;
    }): void {
        const [segments, queryParams] = generatedUriRef ? generatedUriRef.split('?') : [];
        const searchParams = generatedUriRef ? new URLSearchParams(queryParams) : null;

        if (isBatchApplied && searchParams?.has(filterName)) {
            searchParams.delete(QP_PAGE);
            searchParams.delete(filterName);
            if (isDeliveredByFilter(filterName)) {
                searchParams.delete(SHIPPING_TO_FILTER);
            }
            dispatch(
                updateUriRef({
                    filterName,
                    filterValue,
                    uriRef: `${segments}?${decodeURIComponent(searchParams.toString())}`,
                    isDeselected: true,
                    event,
                    ga: undefined,
                })
            );
        } else {
            dispatch(
                updateUriRef({
                    filterName,
                    filterValue,
                    isDeselected: true,
                    event,
                    ga: undefined,
                    uriRef: undefined,
                })
            );
        }
    };
}

const itemSearchFragment = graphql`
    fragment sbSharedAppliedFiltersHelpers_itemSearch on ItemSearchQueryConnection @inline {
        clearAppliedFiltersLinkReference
    }
`;

export function clearAllAppliedFiltersHandler(
    dispatch: Dispatch,
    itemSearchRef: sbSharedAppliedFiltersHelpers_itemSearch$key
) {
    const itemSearch = readInlineData(itemSearchFragment, itemSearchRef);
    return function (event: React.MouseEvent): void {
        dispatch(
            updateUriRef({
                filterName: undefined,
                filterValue: { linkReference: itemSearch?.clearAppliedFiltersLinkReference },
                event,
                ga: undefined,
                uriRef: undefined,
            })
        );
    };
}

type ValidatableAppliedFilterType = {
    canBeDismissed: boolean | null;
};

export const getValidSharedAppliedFilters = <T>(
    appliedFilters: ReadonlyArray<(T & ValidatableAppliedFilterType) | null>,
    additionalFilterRule?: (filter: T & ValidatableAppliedFilterType) => boolean
): ReadonlyArray<(T & ValidatableAppliedFilterType) | null> =>
    appliedFilters?.filter(filter =>
        additionalFilterRule
            ? filter?.canBeDismissed && additionalFilterRule(filter)
            : filter?.canBeDismissed
    ) || [];
